/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import { Box, Button, Text, Flex, Badge, SimpleGrid, useDisclosure } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Info } from 'phosphor-react';
import { DataGrid } from '../../../components/datagrid';
import { SearchFilter } from '../../../components/inputs/filters/search';
import { PageTitle } from '../../../components/PageTitle';
import { useFetch } from '../../../hooks/useFetch';
import { getCellStatus } from '../../../utils/FormatFunctions';
import { useAuth } from '../../../providers/auth';
import { DataSkeleton, ErrorState } from '../../../components/states';
import { Dialog } from '../../../components/ui';

const detailMetrics = (details: any) => [
  { label: 'Materiais extras', value: details.extra_materials_count || 0 },
  { label: 'Templates de materiais', value: details.material_templates_count || 0 },
  { label: 'Categorias', value: details.categories_count || 0 },
  { label: 'Módulos', value: details.school_classes_count || 0 },
  { label: 'Alunos', value: details.students_count || 0 },
  { label: 'Professores', value: details.teachers_count || 0 },
  { label: 'Tutores', value: details.tutors_count || 0 },
  { label: 'Avaliações registradas', value: details.child_ratings_count || 0 },
  {
    label: 'Média por aluno',
    value:
      typeof details.ratings_per_student_avg === 'number'
        ? details.ratings_per_student_avg.toFixed(2)
        : '0.00',
  },
  { label: 'Turmas', value: details.teams_count || 0 },
  { label: 'Encontros', value: details.contents_count || 0 },
  { label: 'Agendamentos', value: details.content_schedules_count || 0 },
  { label: 'Semanas', value: details.weeks_count || 0 },
  { label: 'Média semanas/categoria', value: details.weeks_per_category_avg?.toFixed(2) || '0.00' },
];

export function InstitutionsPage() {
  const baseApiUrl = '/v1/admin';
  const baseUrl = `${baseApiUrl}/institutions`;
  const { requestApi, data, loading, error } = useFetch(baseUrl);
  const { requestApi: reqApiDetails } = useFetch('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const groupIds = user?.group?.id;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedInstitution, setSelectedInstitution] = useState<any>(null);
  const [institutionDetails, setInstitutionDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const handleShowDetails = async (institution: any) => {
    setSelectedInstitution(institution);
    setLoadingDetails(true);
    onOpen();

    try {
      const response = await reqApiDetails(
        'get',
        {},
        `/v1/admin/institutions/${institution.id}/summary`
      );
      setInstitutionDetails(response?.data?.data || response?.data || null);
    } catch {
      setInstitutionDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const columns = [
    { name: 'id', label: 'Id' },
    { name: 'name', label: 'Nome' },
    { name: 'site', label: 'Site' },
    { name: 'responsible', label: 'Responsavel' },
    { name: 'status', label: 'Status' },
    { name: 'details', label: 'Detalhes' },
    { name: 'classes', label: 'Turmas' },
  ];

  return (
    <Box>
      <PageTitle description="Gerencie as instituições, suas turmas e seus responsáveis.">
        Instituições
      </PageTitle>

      <DataGrid
        columns={columns}
        rows={data?.data}
        requestApi={requestApi}
        error={error}
        baseUrl={baseUrl}
        data={data}
        hasCreate={(groupIds !== 7 && groupIds !== 3) ?? true}
        loading={loading}
        rowActions={{
          show: false,
          edit: (user?.group?.id !== 7 && user?.group?.id !== 3) ?? true,
          delete: groupIds !== 3,
        }}
        columnWithAction={[
          {
            name: 'details',
            columnAction: (rowItem) => (
              <Button
                size="xs"
                variant="outline"
                leftIcon={<Info size={14} />}
                onClick={() => handleShowDetails(rowItem)}
              >
                Ver detalhes
              </Button>
            ),
          },
          {
            name: 'classes',
            columnAction: (rowItem) => (
              <Button
                size="xs"
                variant="outline"
                rightIcon={<ArrowRight size={14} />}
                onClick={() => navigate(`${rowItem.id}/teams`)}
              >
                Ver turmas
              </Button>
            ),
          },
          //   {
          //     name: 'status',
          //     columnAction: rowItem => (
          //       <SwitchInput
          //         values={rowItem}
          //         value={rowItem.status}
          //         changeStatus={(checked, values) =>
          //           onToggleSwitch({
          //             requestToggle,
          //             method: 'patch',
          //             checked,
          //             name: 'user',
          //             path: `${baseApiUrl}/globals/status/${values.id}`,
          //             variable: 'status',
          //           })
          //         }
          //       />
          //     ),
          //   },
        ]}
        formatColum={[
          {
            field: 'status',
            formatField: getCellStatus,
          },
        ]}
        headerFilters={[
          {
            name: 'search',
            filterComponent: SearchFilter,
          },
        ]}
      />

      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        title="Detalhes da instituição"
        description={selectedInstitution?.name}
        footer={
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        }
      >
        {loadingDetails && (
          <DataSkeleton variant="detail" rows={8} label="Carregando detalhes da instituição" />
        )}
        {!loadingDetails && institutionDetails && (
          <Box>
            <Flex align="center" gap="8px" mb="16px">
              <Text fontSize="12px" color="content.faint">
                Template
              </Text>
              <Badge
                variant={institutionDetails.template_strategy === 'DEFAULT' ? 'success' : 'info'}
              >
                {institutionDetails.template_strategy === 'DEFAULT' ? 'Padrão' : 'Personalizado'}
              </Badge>
            </Flex>

            <SimpleGrid columns={{ base: 2, md: 3 }} spacing="8px">
              {detailMetrics(institutionDetails).map((metric) => (
                <Box
                  key={metric.label}
                  p="12px"
                  border="1px solid"
                  borderColor="line.hairline"
                  borderRadius="lg"
                >
                  <Text fontSize="12px" lineHeight="16px" color="content.subtle" noOfLines={1}>
                    {metric.label}
                  </Text>
                  <Text
                    mt="2px"
                    fontFamily="display"
                    fontSize="18px"
                    lineHeight="26px"
                    fontWeight={500}
                    color="content.strong"
                  >
                    {metric.value}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>

            {groupIds === 6 && (
              <Box
                mt="16px"
                p="16px"
                bg="state.linkSurface"
                borderRadius="lg"
                border="1px solid"
                borderColor="line.hairline"
              >
                <Text fontSize="13px" fontWeight={500} color="content.strong" mb="8px">
                  Dados de login da instituição
                </Text>
                {institutionDetails.responsible_login ? (
                  <Box>
                    <Text fontSize="13px" color="content.body">
                      {`Responsável: ${institutionDetails.responsible_login.name}`}
                    </Text>
                    <Text fontSize="13px" color="content.body">
                      {`Email de login: ${institutionDetails.responsible_login.email}`}
                    </Text>
                  </Box>
                ) : (
                  <Text fontSize="13px" color="content.subtle">
                    Nenhum responsável cadastrado para esta instituição.
                  </Text>
                )}
              </Box>
            )}
          </Box>
        )}
        {!loadingDetails && !institutionDetails && (
          <ErrorState
            title="Detalhes indisponíveis"
            description="Não foi possível carregar os detalhes da instituição."
            onRetry={() => selectedInstitution && handleShowDetails(selectedInstitution)}
          />
        )}
      </Dialog>
    </Box>
  );
}
